const LOCAL_HTTPS = "https://localhost:8766";
const LOCAL_HTTP = "http://localhost:8766";
const LOCAL_HTTP_IP = "http://127.0.0.1:8766";
const LOCAL_HTTPS_IP = "https://127.0.0.1:8766";
export const WEB_UI = "https://app.beagle.chat";
export const CLI_DESKTOP_URL = "https://beagle.chat/en/desktop/";
const CLI_ORIGINS = [LOCAL_HTTP_IP, LOCAL_HTTP];
const UI_ORIGINS = new Set([LOCAL_HTTPS, LOCAL_HTTP, LOCAL_HTTP_IP, LOCAL_HTTPS_IP, WEB_UI]);
const SESSION_KEY = "beagle-meet-session";
const UI_ORIGIN_KEY = "beagle-meet-ui-origin";
const ADD_REPLY_MS = 120_000;

function clientHint() {
  let client = "web";
  try {
    if (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("client") === "beagle-app") {
      client = "beagle-app";
    }
  } catch {
    /* ignore */
  }
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  let platform = "web";
  if (/iPhone|iPad|iPod/i.test(ua)) platform = "ios";
  else if (/Android/i.test(ua)) platform = "android";
  return { client, platform };
}

function rememberUiOrigin(origin, userid) {
  try {
    if (typeof sessionStorage !== "undefined" && origin) sessionStorage.setItem(UI_ORIGIN_KEY, origin);
  } catch {
    /* ignore */
  }
  try {
    if (typeof localStorage === "undefined" || !origin) return;
    localStorage.setItem(UI_ORIGIN_KEY, origin);
    if (userid) localStorage.setItem(`${UI_ORIGIN_KEY}:${userid}`, origin);
  } catch {
    /* ignore */
  }
}

export function lastUiOrigin(userid) {
  try {
    const session = typeof sessionStorage !== "undefined" ? sessionStorage.getItem(UI_ORIGIN_KEY) : "";
    if (session) return session;
    if (typeof localStorage === "undefined") return "";
    if (userid) {
      const per = localStorage.getItem(`${UI_ORIGIN_KEY}:${userid}`);
      if (per) return per;
    }
    return localStorage.getItem(UI_ORIGIN_KEY) || "";
  } catch {
    return "";
  }
}

/** Same Beagle they signed in with. Web sign-in → app.beagle.chat; CLI → :8766. */
export function pickAddOrigin(via, userid) {
  const remembered = lastUiOrigin(userid);
  if (via === "web" || remembered === WEB_UI) return WEB_UI;
  if (via === "cli" || remembered === LOCAL_HTTP_IP || remembered === LOCAL_HTTP) return LOCAL_HTTP_IP;
  throw new Error("Sign in with Beagle again so we know which client to open (app or CLI).");
}

export function isCarrierAddress(value) {
  const s = String(value || "").trim();
  return s.length >= 50 && s.length <= 60 && /^[1-9A-HJ-NP-Za-km-z]+$/.test(s);
}

function trimBase(apiBase) {
  return String(apiBase || "").replace(/\/+$/, "");
}

function url(apiBase, path) {
  const b = trimBase(apiBase);
  return b ? `${b}${path}` : path;
}

export function sessionHeaders(sessionToken) {
  const h = { "Content-Type": "application/json" };
  const token = sessionToken || (typeof sessionStorage !== "undefined" ? sessionStorage.getItem(SESSION_KEY) : "");
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

export function rememberSession(token) {
  if (typeof sessionStorage === "undefined") return;
  if (token) sessionStorage.setItem(SESSION_KEY, token);
  else sessionStorage.removeItem(SESSION_KEY);
}

/** RN WebView host. `client=beagle-app` is the contract; ReactNativeWebView is a fallback. Do not use User-Agent. */
export function isNativeMeetHost() {
  if (typeof window === "undefined") return false;
  try {
    if (new URLSearchParams(window.location.search).get("client") === "beagle-app") return true;
  } catch {
    /* ignore */
  }
  return typeof window.ReactNativeWebView?.postMessage === "function";
}

export function postToNativeHost(payload) {
  const msg = JSON.stringify(payload);
  try {
    window.ReactNativeWebView?.postMessage(msg);
  } catch {
    /* not in RN */
  }
  try {
    if (window.parent && window.parent !== window) window.parent.postMessage(payload, "*");
  } catch {
    /* ignore */
  }
}

export function savedSession() {
  if (typeof sessionStorage === "undefined") return "";
  return sessionStorage.getItem(SESSION_KEY) || "";
}

export async function fetchMe(apiBase) {
  const res = await fetch(url(apiBase, "/api/auth/me"), {
    credentials: "include",
    headers: sessionHeaders(),
  });
  if (!res.ok) return { authenticated: false };
  return res.json();
}

export async function logout(apiBase) {
  rememberSession("");
  try {
    sessionStorage.removeItem(UI_ORIGIN_KEY);
  } catch {
    /* ignore */
  }
  await fetch(url(apiBase, "/api/auth/me"), {
    method: "POST",
    credentials: "include",
    headers: sessionHeaders(),
  });
}

async function reachable(origin) {
  try {
    const r = await fetch(`${origin}/api/state`, {
      method: "GET",
      mode: "cors",
      credentials: "omit",
      signal: AbortSignal.timeout(1500),
    });
    return r.ok;
  } catch {
    /* older Beagle with no CORS — only works from loopback pages */
  }
  try {
    await fetch(`${origin}/connect`, {
      method: "GET",
      mode: "no-cors",
      credentials: "omit",
      signal: AbortSignal.timeout(800),
    });
    return true;
  } catch {
    return false;
  }
}

function pageIsLoopback() {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1" || h === "[::1]";
}

export async function probeCli() {
  const found = (
    await Promise.all(CLI_ORIGINS.map(async (o) => ((await reachable(o)) ? o : null)))
  ).find(Boolean);
  if (found) return { status: "up", origin: found };
  // Public hosts (dev.fi.chat, snoopy.fi.chat) cannot fetch loopback unless
  // the daemon sends Private-Network CORS. A failed probe is not "down".
  if (!pageIsLoopback()) return { status: "opaque", origin: LOCAL_HTTP_IP };
  return { status: "down", origin: null };
}

export async function pickCliOrigin() {
  return LOCAL_HTTP_IP;
}

export async function isCliAvailable() {
  return (await probeCli()).status !== "down";
}

export function openDesktopDownload() {
  window.open(CLI_DESKTOP_URL, "_blank", "noopener,noreferrer");
}

/** Popup + server verify. Matches interview.network DecentSignIn.js (GitHub).
 *  source: "web" (app.beagle.chat) or "cli" (localhost:8766). */
export function signInWithDecent(apiBase, source = "web") {
  return new Promise(async (resolve, reject) => {
    const uiOrigin = source === "cli" ? LOCAL_HTTP_IP : WEB_UI;
    rememberUiOrigin(uiOrigin);
    // Open in the same tick as the click so the popup is not blocked, then
    // point it at /connect after the nonce is ready.
    const win = window.open("about:blank", "decent-auth", "width=420,height=520");

    let nonce;
    try {
      const r = await fetch(url(apiBase, "/api/auth/nonce"), { credentials: "include" });
      nonce = (await r.json()).nonce;
      if (!nonce) throw new Error("no nonce");
    } catch (err) {
      try {
        win?.close();
      } catch {
        /* ignore */
      }
      reject(err instanceof Error ? err : new Error("Failed to get login nonce"));
      return;
    }

    const origin = window.location.origin;
    const connect = `${uiOrigin}/connect?origin=${encodeURIComponent(origin)}&nonce=${encodeURIComponent(nonce)}`;
    if (!win) {
      reject(new Error("Popup blocked — allow popups and retry"));
      return;
    }
    try {
      win.location.href = connect;
    } catch {
      reject(new Error(source === "cli" ? "Could not open localhost:8766" : "Could not open app.beagle.chat"));
      return;
    }

    let settled = false;
    const finish = (err, data) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", onMessage);
      if (err) reject(err);
      else resolve(data);
    };

    const onMessage = async (e) => {
      if (!UI_ORIGINS.has(e.origin)) return;
      if (!e.data || e.data.type !== "decent-auth") return;
      if (e.data.error) {
        finish(new Error(e.data.error === "denied" ? "Sign-in cancelled" : String(e.data.error)));
        return;
      }
      const { userid, nonce: gotNonce, sig } = e.data;
      if (gotNonce !== nonce) {
        finish(new Error("Nonce mismatch"));
        return;
      }
      try {
        const r = await fetch(url(apiBase, "/api/auth/decent"), {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userid,
            nonce,
            sig,
            origin,
            name: e.data.name,
            avatar: e.data.avatar,
            punkId: e.data.punkId,
            address: e.data.address || e.data.carrierAddress,
            via: source,
            ...clientHint(),
          }),
        });
        const d = await r.json();
        if (!r.ok || !d.ok) {
          finish(new Error(d.error || `Verify failed (${r.status})`));
          return;
        }
        if (d.token) rememberSession(d.token);
        rememberUiOrigin(uiOrigin, d.userid || userid);
        finish(null, d);
      } catch (err) {
        finish(err instanceof Error ? err : new Error("Network error"));
      }
    };

    window.addEventListener("message", onMessage);
    setTimeout(() => {
      if (settled) return;
      if (!win || win.closed) finish(new Error("Popup blocked — allow popups and retry"));
      else if (source === "cli") {
        finish(new Error("No response from Beagle CLI. Confirm it is running on localhost:8766."));
      } else {
        finish(new Error("No response from app.beagle.chat. Retry or use CLI."));
      }
    }, 20000);
  });
}

// ── arriving from Beagle's Apps tab ─────────────────────────────────────────
//
// Someone who opened Beagle Meet from inside their own Beagle is already
// signed in; asking them again is absurd. Beagle puts a short-lived assertion
// in the URL FRAGMENT — never sent to a server, never in a Referer, never in
// an access log — and this reads it, hands it to our server, and strips it
// from the address bar so a copied URL carries no credential.
//
// Deliberately not using @decentnetwork/beagle-connect's readLaunch() here:
// this bundle has no dependency on @decentnetwork/peer and pulling one in for
// a client-side pre-check would cost more than it buys. The signature is
// verified where it matters — on the server, in /api/auth/beagle-launch.
// Everything below is parsing and freshness, not trust.
const LAUNCH_MAX_AGE_MS = 120_000;

function stripLaunchFragment() {
  try {
    const rest = (window.location.hash || "")
      .replace(/(?:^|[#&])beagle=[A-Za-z0-9_-]+/, "")
      .replace(/^#?&?/, "");
    window.history.replaceState(null, "", window.location.pathname + window.location.search + (rest ? `#${rest}` : ""));
  } catch { /* stripping is hygiene, not correctness */ }
}

/** Drop an Apps-tab assertion we are not going to spend. A copied URL must
 *  not keep the fragment, but a meeting that already has a session must not
 *  mint a new one from it either. */
export function discardLaunchFragment() {
  if (/(?:^|[#&])beagle=/.test(window.location.hash || "")) stripLaunchFragment();
}

function readLaunchFragment() {
  const m = /(?:^|[#&])beagle=([A-Za-z0-9_-]+)/.exec(window.location.hash || "");
  if (!m) return null;
  stripLaunchFragment();
  try {
    const b64 = m[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(decodeURIComponent(escape(atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4)))));
  } catch {
    return null;
  }
}

/** Consume a Beagle launch, if there is one. Returns the session payload, or
 *  null when there was nothing to consume. Throws only on a server refusal,
 *  which is worth showing: a stale or replayed assertion is a real answer. */
export async function signInFromLaunch(apiBase) {
  const a = readLaunchFragment();
  if (!a || !a.userid || !a.sig || !Number.isFinite(Number(a.ts))) return null;
  if (Math.abs(Date.now() - Number(a.ts)) > LAUNCH_MAX_AGE_MS) return null;

  const r = await fetch(url(apiBase, "/api/auth/beagle-launch"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      userid: a.userid,
      ts: a.ts,
      sig: a.sig,
      name: a.name,
      avatar: a.avatar,
      punkId: a.punkId,
      address: a.address || a.carrierAddress,
      // Our own origin, so the server checks the assertion was minted for US.
      origin: window.location.origin,
      ...clientHint(),
    }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok || !d.ok) throw new Error(d.error || `Beagle launch rejected (${r.status})`);
  if (d.token) rememberSession(d.token);
  return d;
}

/** Ask the visitor's Beagle to send a friend request to `address`.
 *  Opens the same client they signed in with — app.beagle.chat or :8766 —
 *  never probed, never guessed. `win` must be opened in the click handler. */
export async function addBeagleFriend({ address, name, win, via, userid, uiOrigin }) {
  if (!isCarrierAddress(address)) throw new Error("No Beagle address for this person");
  const destOrigin = uiOrigin || pickAddOrigin(via, userid);
  const nonce = crypto.randomUUID();
  const popup = win || window.open("about:blank", "beagle-connect", "width=420,height=560");
  if (!popup) throw new Error("Popup blocked — allow popups and retry");
  const origin = window.location.origin;
  const dest = new URL("/connect", destOrigin);
  dest.searchParams.set("action", "add");
  dest.searchParams.set("address", String(address).trim());
  if (name) dest.searchParams.set("name", name);
  dest.searchParams.set("origin", origin);
  dest.searchParams.set("nonce", nonce);
  try {
    popup.location.href = dest.toString();
  } catch {
    try {
      popup.close();
    } catch {
      /* ignore */
    }
    throw new Error(destOrigin === WEB_UI ? "Could not open app.beagle.chat" : "Could not open localhost:8766");
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (err, val) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", onMessage);
      clearInterval(watch);
      clearTimeout(timer);
      err ? reject(err) : resolve(val);
    };
    const onMessage = (e) => {
      if (!UI_ORIGINS.has(e.origin)) return;
      const d = e.data;
      if (!d || d.type !== "beagle-action" || d.action !== "add" || d.nonce !== nonce) return;
      if (d.error) {
        finish(new Error(d.error === "denied" ? "Cancelled" : String(d.error)));
        return;
      }
      finish(null, d);
    };
    window.addEventListener("message", onMessage);
    const watch = setInterval(() => {
      if (popup.closed) finish(new Error("Cancelled"));
    }, 400);
    const timer = setTimeout(() => {
      finish(new Error(popup.closed ? "Cancelled" : "Beagle did not answer."));
    }, ADD_REPLY_MS);
  });
}
